import {
  DashboardWidgetError,
  DashboardWidgetLoading,
} from '@/components/custom/DashboardWidgetState';
import { StatCard } from '@/components/custom/StatCard';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdminApi } from '@/hooks/useAdminApi';
import { toApiError } from '@/services/api';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Building2,
  Clock,
  DollarSign,
  Dumbbell,
  Shield,
  Trophy,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import AdminAffiliates from './AdminAffiliates';
import AdminAgentLeads from './AdminAgentLeads';
import AdminAnalytics from './AdminAnalytics';
import AdminAuditLogs from './AdminAuditLogs';
import AdminCommissions from './AdminCommissions';
import AdminContent from './AdminContent';
import AdminEvents from './AdminEvents';
import AdminPermissions from './AdminPermissions';
import AdminProgramming from './AdminProgramming';
import AdminScores from './AdminScores';
import AdminStandards from './AdminStandards';
import AdminStripeConsole from './AdminStripeConsole';
import AdminSubscriptions from './AdminSubscriptions';
import AdminSyncHealth from './AdminSyncHealth';
import AdminTeam from './AdminTeam';
import AdminTerritories from './AdminTerritories';
import AdminTracks from './AdminTracks';
import AdminUsers from './AdminUsers';

function AdminDashboardHome() {
  const adminApi = useAdminApi();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: async () => {
      const response = await adminApi.getDashboard();
      if (!response.success) throw toApiError(response, 'Failed to load dashboard');
      return response.data;
    },
    refetchInterval: 60000,
  });

  const {
    data: activityData,
    isLoading: isActivityLoading,
    isFetching: isActivityFetching,
    error: activityError,
    refetch: refetchActivity,
  } = useQuery({
    queryKey: ['admin-activity-feed'],
    queryFn: async () => {
      const response = await adminApi.getActivityFeed({ limit: 10 });
      if (!response.success) throw toApiError(response, 'Failed to load recent activity');
      return response.data;
    },
    refetchInterval: 60000,
  });

  const overview = data?.overview;
  const growth = data?.growth;
  const commissions = data?.commissions;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">HQ Admin Dashboard</h1>
        <p className="mt-1 text-muted-foreground">ErgShoot Headquarters | System Administration</p>
      </div>

      {/* Stats */}
      {isLoading ? (
        <DashboardWidgetLoading label="Loading platform overview…" />
      ) : error ? (
        <DashboardWidgetError
          title="Platform overview unavailable"
          description="We couldn't load the latest platform metrics. Administrative shortcuts and recent activity remain available."
          error={error}
          context={{ portal: 'admin', widget: 'overview', endpoint: '/admin/dashboard' }}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Affiliates"
            value={overview?.total_affiliates || 0}
            subtitle="Active gyms"
            icon={Building2}
            delay={0}
            href="/admin/affiliates"
          />
          <StatCard
            title="Pending Approvals"
            value={growth?.pending_approvals || 0}
            subtitle="Awaiting review"
            icon={Clock}
            delay={50}
            href="/admin/affiliates?status=pending"
          />
          <StatCard
            title="Total Agents"
            value={overview?.total_agents || 0}
            subtitle="Territory partners"
            icon={UserCheck}
            delay={100}
            href="/admin/users?role=agent"
          />
          <StatCard
            title="Total Members"
            value={overview?.total_members || 0}
            subtitle="Active athletes"
            icon={Users}
            delay={150}
            href="/admin/users?role=member"
          />
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="animate-fade-in" style={{ animationDelay: '200ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Clock className="h-5 w-5 text-primary" />
              Affiliate Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Review and approve pending affiliate applications.
              {growth?.pending_approvals && growth.pending_approvals > 0 && (
                <span className="ml-2 text-orange-500 font-medium">
                  ({growth.pending_approvals} pending)
                </span>
              )}
            </p>
            <Link to="/admin/affiliates?status=pending">
              <Button
                variant={
                  growth?.pending_approvals && growth.pending_approvals > 0 ? 'default' : 'outline'
                }
                className="w-full"
              >
                Manage Affiliates
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '250ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Dumbbell className="h-5 w-5 text-primary" />
              Programming
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Upload and manage workout programming.
            </p>
            <Link to="/admin/programming">
              <Button variant="outline" className="w-full">
                Manage Programming
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '300ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Shield className="h-5 w-5 text-primary" />
              User Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">View and manage system users.</p>
            <Link to="/admin/users">
              <Button variant="outline" className="w-full">
                Manage Users
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Commission Summary + Activity Feed (HQ-002) */}
      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        {/* Commission Summary */}
        {!isLoading && !error && (
          <Card className="animate-fade-in" style={{ animationDelay: '350ms' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Commission Summary
              </CardTitle>
              <CardDescription>Agent commission overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 grid-cols-2">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold mt-1">
                    ${((commissions?.pending_amount || 0) / 100).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {commissions?.pending_payouts || 0} payouts
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Paid</p>
                  <p className="text-2xl font-bold mt-1 text-green-500">
                    ${((commissions?.paid_amount || 0) / 100).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">All time</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity */}
        <Card className="animate-fade-in" style={{ animationDelay: '400ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Recent Activity
            </CardTitle>
            <CardDescription>Latest platform events</CardDescription>
          </CardHeader>
          <CardContent>
            {isActivityLoading ? (
              <DashboardWidgetLoading embedded label="Loading recent activity…" />
            ) : activityError ? (
              <DashboardWidgetError
                embedded
                title="Recent activity unavailable"
                description="We couldn't load the latest platform events."
                error={activityError}
                context={{
                  portal: 'admin',
                  widget: 'recent-activity',
                  endpoint: '/admin/activity-feed',
                }}
                onRetry={() => void refetchActivity()}
                isRetrying={isActivityFetching}
              />
            ) : activityData?.activities && activityData.activities.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {activityData.activities.map((activity) => {
                  const ActivityIcon =
                    activity.type === 'user_signup'
                      ? UserPlus
                      : activity.type === 'affiliate_update'
                        ? Building2
                        : Trophy;
                  const iconColor =
                    activity.type === 'user_signup'
                      ? 'text-blue-500'
                      : activity.type === 'affiliate_update'
                        ? 'text-green-500'
                        : 'text-orange-500';

                  return (
                    <div
                      key={`${activity.type}-${activity.id}`}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50"
                    >
                      <ActivityIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm truncate">{activity.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(activity.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {activity.type === 'user_signup'
                          ? 'Signup'
                          : activity.type === 'affiliate_update'
                            ? 'Affiliate'
                            : 'Score'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No recent activity</p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export default function AdminDashboard() {
  return (
    <Routes>
      <Route path="/" element={<AdminDashboardHome />} />
      <Route path="affiliates" element={<AdminAffiliates />} />
      <Route path="programming" element={<AdminProgramming />} />
      <Route path="standards" element={<AdminStandards />} />
      <Route path="tracks" element={<AdminTracks />} />
      <Route path="content" element={<AdminContent />} />
      <Route path="users" element={<AdminUsers />} />
      <Route path="agents" element={<Navigate to="/admin/users?role=agent" replace />} />
      <Route path="agent-leads" element={<AdminAgentLeads />} />
      <Route path="analytics" element={<AdminAnalytics />} />
      <Route path="audit-logs" element={<AdminAuditLogs />} />
      <Route path="commissions" element={<AdminCommissions />} />
      <Route path="stripe-console" element={<AdminStripeConsole />} />
      <Route path="subscriptions" element={<AdminSubscriptions />} />
      <Route path="permissions" element={<AdminPermissions />} />
      <Route path="team" element={<AdminTeam />} />
      <Route path="territories" element={<AdminTerritories />} />
      <Route path="scores" element={<AdminScores />} />
      <Route path="events" element={<AdminEvents />} />
      <Route path="sync" element={<AdminSyncHealth />} />
    </Routes>
  );
}
